import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { usePostDocumentsMutation } from "src/core/api/documents";
import { useFolder } from "src/core/contexts/FolderContext";
import { computeStakeholderIncompleteFields } from "src/core/forms/folder/folder.incomplete";
import {
  defaultUpdateEntrepreneurValues,
  UpdateEntrepreneurFormValues,
  updateEntrepreneurSchema,
} from "src/core/forms/stakeholder/updateEntrepreneur.schema";
import {
  useCreateOrReplaceExternalDocumentMutation,
  useDeleteDocumentMutation,
  useDeleteExternalDocumentMutation,
  useSearchCustomerFolderByStringLazyQuery,
  useUpdateDocumentMutation,
  useUpdateStakeholderMutation,
} from "src/core/gql/graphql";
import { useRequiredDocuments } from "src/core/hooks/common/useRequiredDocuments";
import { useEReputation } from "src/core/hooks/folder/useEReputation";
import { useUploadDocumentsErrorToast } from "src/core/hooks/folder/useUploadDocumentsErrorToast";
import { useToast } from "src/core/hooks/use-toast";
import { ICustomDuplicateFolder } from "src/core/interfaces/Folders";
import { getDefaultDocuments } from "src/core/mappers/document.mapper";
import { mapFolderFilesToDuplicateFolders } from "src/core/mappers/folder.mapper";
import { deleteDocuments, uploadDocuments } from "src/core/services/document.service";
import { Document } from "src/core/types/document";
import { toIsoUtcFromDate } from "src/core/utils/date";
import { getDocumentsExpirationUpdates } from "src/core/utils/documents/getDocumentsExpirationUpdates";
import { buildUpdateEIVariables } from "src/core/utils/ei";
import {
  calculateCompanyMissingDocuments,
  calculateEIMissingFields,
} from "src/core/utils/folderAnalysis";

export const useUpdatePPH = ({ onSuccess }: { onSuccess?: () => void }) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [searchByregistration] = useSearchCustomerFolderByStringLazyQuery(); // Permet d'appeler la mutation de création d'un stakeholder
  const [updateStakeholder] = useUpdateStakeholderMutation();
  const [postDocuments] = usePostDocumentsMutation();
  const [createOrReplaceExternalDocument] = useCreateOrReplaceExternalDocumentMutation();

  const [deleteDocument] = useDeleteDocumentMutation();
  const [deleteExternalDocument] = useDeleteExternalDocumentMutation();
  const [updateDocument] = useUpdateDocumentMutation();
  const { id: folderId } = useParams();
  const [duplicateFolder, setDuplicateFolder] = useState<ICustomDuplicateFolder | undefined>();
  const [incompleteFields, setIncompleteFields] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [defaultDocuments, setDefaultDocuments] = useState<Record<string, Document>>({});

  const { folderFile, isReadOnly, refetch } = useFolder();

  const customer = folderFile.folderFile?.fileCustomerFolder?.customer;
  const form = useForm<UpdateEntrepreneurFormValues>({
    resolver: zodResolver(updateEntrepreneurSchema()),
    defaultValues: defaultUpdateEntrepreneurValues(folderFile),
  });

  const requiredDocuments = useRequiredDocuments(folderFile, customer?.id);
  const { handleUploadErrors } = useUploadDocumentsErrorToast({ requiredDocuments, toast });

  const { callSafely: callEReputationSafely } = useEReputation();

  const missingInfo = calculateEIMissingFields(folderFile);
  const missingDocuments = calculateCompanyMissingDocuments(folderFile);

  useEffect(() => {
    if (!customer?.id) return;

    const defaultDoc = getDefaultDocuments(folderFile, customer.id, requiredDocuments);
    setDefaultDocuments(defaultDoc);
    form.reset({
      ...defaultUpdateEntrepreneurValues(folderFile),
      documents: defaultDoc,
    });

    setIncompleteFields(computeStakeholderIncompleteFields(form.getValues()));
  }, [requiredDocuments, folderFile, form, customer]);

  const onSubmit = useCallback(
    async (data: UpdateEntrepreneurFormValues) => {
      //vérifier si le registration existe
      if (data.registration) {
        const { data: clientData } = await searchByregistration({
          variables: {
            searchString: data.registration,
          },
        });

        const totalCount = clientData?.customerFoldersPage?.pageInfo?.totalCount ?? 0;
        if (totalCount > 0 && clientData) {
          const duplicateFolders = mapFolderFilesToDuplicateFolders(clientData);
          if (
            duplicateFolders.length > 0 &&
            duplicateFolders[0].id !== folderId &&
            duplicateFolders[0].siren === data.registration
          ) {
            setDuplicateFolder(duplicateFolders[0]);
            form.setError("registration", {
              type: "duplicate",
              message: t("form.createClient.error.registrationAlreadyExists"),
            });
            return;
          }
        }
      }

      if (customer?.id && folderId) {
        try {
          setIsLoading(true);

          const variables = buildUpdateEIVariables(customer.id, data);

          // Détecter les documents dont l'expirationDate a changé
          const documentsToUpdate = getDocumentsExpirationUpdates(data.documents, defaultDocuments);

          // 1) On lance les opérations “métier” (hors upload)
          await Promise.all([
            updateStakeholder({ variables }),
            deleteDocuments(
              data.documents,
              defaultDocuments,
              deleteDocument,
              deleteExternalDocument
            ),
            ...documentsToUpdate.map(({ fileId, expirationDate }) =>
              updateDocument({
                variables: {
                  id: fileId,
                  expirationDate: expirationDate
                    ? toIsoUtcFromDate(expirationDate, "yyyy-MM-dd")
                    : undefined,
                },
              })
            ),
          ]);

          // 2) Upload des documents (résultats par document)
          const uploadResults = await uploadDocuments(
            data.documents,
            customer.id,
            postDocuments,
            createOrReplaceExternalDocument
          );

          const failedUploads = uploadResults.filter((r) => !r.ok);

          // Affichage d'un toast d'erreur si des documents n'ont pas pu être uploadés
          handleUploadErrors(failedUploads);

          // 3) Appel à eReputation pour que le screening se fasse après la mise à jour du stakeholder.
          await callEReputationSafely({
            folderFileId: folderId,
            logContext: "useUpdatePPH::onSubmit",
          });

          // Réinitialiser le formulaire avec les nouvelles données afin de recalculer le nombre de champs manquants.
          form.reset(data);

          await refetch();

          toast({ description: t("form.addStakeholderFull.success") });

          onSuccess?.();

          // Gestion du cas des champs requis pour la validation du dossier, mais pas nécessaire pour l'enregistrement.
          setIncompleteFields(computeStakeholderIncompleteFields(data));
        } catch (error) {
          console.error("useUpdatePPH::error", error);
        } finally {
          setIsLoading(false);
        }
      }
    },
    [
      customer?.id,
      folderId,
      form,
      refetch,
      t,
      onSuccess,
      defaultDocuments,
      deleteDocument,
      deleteExternalDocument,
      createOrReplaceExternalDocument,
      updateDocument,
      postDocuments,
      searchByregistration,
      updateStakeholder,
      callEReputationSafely,
      handleUploadErrors,
      toast,
    ]
  );

  return {
    form,
    onSubmit,
    isReadOnly,
    requiredDocuments,
    incompleteFields,
    isLoading,
    missingInfo,
    missingDocuments,
    duplicateFolder,
  };
};
